import { useCallback, useEffect, useRef } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { LeftArrow } from '@styled-icons/boxicons-solid';
import { preload } from 'react-dom';

export default function useComicImageAnimations({
  playerState,
  detectedCues,
  animations,
  onAnimationsReady,
  reset,
}) {
  const { state, actions } = useCustomHook({
    defaultState: {
      visibleAnimations: [],
      reRenderKey: 0,
      staticImages: [],
      currentAnimationId: -1,
      preloadStatus: {
        total: 0,
        loaded: 0,
        failed: [],
      },
    },
    actions: {},
  });

  const preloadedAnimationsRef = useRef(new Map());
  const preloadedFirstFramesRef = useRef(new Map());

  const preloadSingleAnimation = useCallback(
    (animation) => {
      return new Promise((resolve, reject) => {
        // Skip if already preloaded
        if (preloadedAnimationsRef.current.has(animation.conf.boxId)) {
          actions.setState({
            preloadStatus: {
              ...state.preloadStatus,
              loaded: state.preloadStatus.loaded + 1,
            },
          });
          resolve({ boxId: animation.conf.boxId, type: 'animation' });
          return;
        }

        const img = new Image();

        img.onload = () => {
          preloadedAnimationsRef.current.set(animation.conf.boxId, img);
          actions.setState({
            preloadStatus: {
              ...state.preloadStatus,
              loaded: state.preloadStatus.loaded + 1,
            },
          });
          resolve({ boxId: animation.conf.boxId, type: 'animation' });
        };

        img.onerror = (error) => {
          actions.setState({
            preloadStatus: {
              ...state.preloadStatus,
              failed: [...state.preloadStatus.failed, animation.conf.boxId],
            },
          });
          reject(
            new Error(
              `Animation ${animation.conf.boxId} failed to load: ${error}`
            )
          );
        };

        // Force the browser to load the full animation
        img.style.position = 'absolute';
        img.style.opacity = '0';
        img.style.pointerEvents = 'none';
        document.body.appendChild(img);
        img.src = animation.conf.file;
      });
    },
    [state.preloadStatus.loaded]
  );

  const preloadSingleFirstFrame = useCallback(
    (animation) => {
      return new Promise((resolve, reject) => {
        if (preloadedFirstFramesRef.current.has(animation.conf.boxId)) {
          actions.setState({
            preloadStatus: {
              ...state.preloadStatus,
              loaded: state.preloadStatus.loaded + 1,
            },
          });
          resolve({ boxId: animation.conf.boxId, type: 'firstFrame' });
          return;
        }

        const img = new Image();

        img.onload = () => {
          preloadedFirstFramesRef.current.set(animation.conf.boxId, img);
          actions.setState({
            preloadStatus: {
              ...state.preloadStatus,
              loaded: state.preloadStatus.loaded + 1,
            },
          });
          resolve({ boxId: animation.conf.boxId, type: 'firstFrame' });
        };

        img.onerror = (error) => {
          reject(
            new Error(
              `First frame ${animation.conf.boxId} failed to load: ${error}`
            )
          );
        };

        img.src = animation.conf.firstFrameFile;
      });
    },
    [state.preloadStatus.loaded]
  );

  const preloadAnimations = useCallback(async () => {
    if (!animations?.length) return;

    actions.setState({
      preloadStatus: {
        total: animations.length * 2, // Both animations and first frames
        loaded: 0,
        failed: [],
      },
    });

    try {
      // Preload all animations concurrently
      const preloadPromises = animations.flatMap((animation) => [
        preloadSingleAnimation(animation),
        preloadSingleFirstFrame(animation),
      ]);

      await Promise.all(preloadPromises);

      console.log('All animations preloaded successfully');
      if (onAnimationsReady) onAnimationsReady();
    } catch (error) {
      console.error('Error during preload:', error);
      // Continue even if some animations fail
      if (onAnimationsReady) onAnimationsReady();
    }
  }, [animations, preloadSingleAnimation, preloadSingleFirstFrame]);

  useEffect(() => {
    preloadAnimations();

    return () => {
      document
        .querySelectorAll('img[style*="opacity: 0"]')
        .forEach((el) => el.remove());

      preloadedAnimationsRef.current.clear();
      preloadedFirstFramesRef.current.clear();
    };
  }, []);

  useEffect(() => {
    actions.setState({
      staticImages: animations.map((animation) => ({
        src: animation.conf.firstFrameFile,
        boxId: animation.conf.boxId,
      })),
    });
  }, []);

  useEffect(() => {
    if (!detectedCues) return;

    for (const cue of detectedCues) {
      if (cue.status == 'STARTING' && cue.type == 'page') {
        actions.setState({
          visibleAnimations: [],
        });
      }
      if (cue.status == 'STARTING' && cue.type == 'box') {
        const cuedAnimation = animations.find(
          (animation) => animation.conf.boxId === cue.boxId
        );
        if (cuedAnimation) {
          const currentAnimationData = {
            src: cuedAnimation.conf.file,
            boxId: cuedAnimation.conf.boxId,
          };
          actions.setState({
            visibleAnimations: [
              ...state.visibleAnimations,
              currentAnimationData,
            ],
          });
        }
      }
    }
  }, [detectedCues]);

  useEffect(() => {
    if (reset) {
      actions.setState({
        visibleAnimations: [],
        reRenderKey: state.reRenderKey + 1,
        staticImages: animations.map((animation) => ({
          src: animation.conf.firstFrameFile,
          boxId: animation.conf.boxId,
        })),
        currentAnimationId: -1,
      });
    }
  }, [reset]);

  const handleAnimationLoad = useCallback((boxId) => {
    actions.setState({
      currentAnimationId: boxId,
    });
  }, []);

  return {
    state,
    actions,
    handleAnimationLoad,
  };
}
